import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import {
	useAdminUser,
	useAdminUserAudit,
	useAdminUserMfa,
	useAdminUserSessions,
	useForceLogoutUser,
	useReactivateUser,
	useResetMfaUser,
	useSuspendUser,
} from "@/hooks/use-admin-users";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { cn } from "@/lib/cn";

type Tab = "identity" | "permissions" | "sessions" | "audit";

export function UserDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [tab, setTab] = useState<Tab>("identity");
	const [actionMsg, setActionMsg] = useState<string | null>(null);

	const { data: user, refetch } = useAdminUser(id);
	const { data: sessions } = useAdminUserSessions(
		tab === "sessions" ? id : undefined,
	);
	const { data: audit } = useAdminUserAudit(tab === "audit" ? id : undefined);
	const { data: mfa } = useAdminUserMfa(id);

	const suspendMutation = useSuspendUser();
	const reactivateMutation = useReactivateUser();
	const resetMfaMutation = useResetMfaUser();
	const forceLogoutMutation = useForceLogoutUser();

	if (!user) return <div className="muted">Chargement…</div>;

	const run = async (fn: () => Promise<unknown>, ok: string) => {
		setActionMsg(null);
		try {
			await fn();
			setActionMsg(ok);
			refetch();
		} catch (err) {
			setActionMsg(extractErrorMessage(err, "Action impossible"));
		}
	};

	return (
		<>
			<div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
				<a
					className="root"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/administration")}
				>
					Administration
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<a
					className="root"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/administration")}
				>
					Utilisateurs
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<span className="here">{user.fullName}</span>
			</div>

			<PageHead
				title={user.fullName}
				sub={`${user.email} · ${user.role.label} · Créé le ${new Date(user.createdAt).toLocaleDateString("fr-FR")}`}
				actions={
					<>
						<AdminButton
							variant="ghost"
							onClick={() =>
								navigate(`/administration/utilisateurs/${user.id}/modifier`)
							}
							data-testid="user-edit"
						>
							<AdminIcon n="edit" s={14} />
							Modifier
						</AdminButton>
						{user.status === "active" && (
							<AdminButton
								variant="ghost"
								style={{ color: "var(--a-warning)" }}
								onClick={() =>
									void run(
										() => suspendMutation.mutateAsync(user.id),
										"Compte suspendu",
									)
								}
								data-testid="user-suspend"
							>
								<AdminIcon n="alert" s={14} />
								Suspendre
							</AdminButton>
						)}
						{user.status === "suspended" && (
							<AdminButton
								variant="primary"
								onClick={() =>
									void run(
										() => reactivateMutation.mutateAsync(user.id),
										"Compte réactivé",
									)
								}
								data-testid="user-reactivate"
							>
								<AdminIcon n="check" s={14} />
								Réactiver
							</AdminButton>
						)}
						<AdminButton
							variant="ghost"
							onClick={() =>
								void run(
									() => forceLogoutMutation.mutateAsync(user.id),
									"Sessions révoquées",
								)
							}
							data-testid="user-force-logout"
						>
							<AdminIcon n="refresh" s={14} />
							Force logout
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() =>
								void run(
									() => resetMfaMutation.mutateAsync(user.id),
									"MFA réinitialisée",
								)
							}
							data-testid="user-reset-mfa"
						>
							<AdminIcon n="key" s={14} />
							Reset MFA
						</AdminButton>
					</>
				}
			/>

			{actionMsg && (
				<div
					className="auth-alert success"
					data-testid="user-action-msg"
					style={{ marginBottom: 14 }}
				>
					<span className="icon">
						<AdminIcon n="check" s={16} />
					</span>
					<div>{actionMsg}</div>
				</div>
			)}

			{/* Header summary card */}
			<Card style={{ marginBottom: 16 }}>
				<CardBody>
					<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
						<AdminAvatar name={user.fullName} size="lg" />
						<div style={{ flex: 1 }}>
							<div
								style={{
									display: "flex",
									gap: 8,
									alignItems: "center",
									marginBottom: 4,
								}}
							>
								<strong
									style={{ fontFamily: "var(--a-font-display)", fontSize: 18 }}
								>
									{user.fullName}
								</strong>
								<Tag
									variant={user.role.key === "super_admin" ? "terra" : "info"}
								>
									{user.role.label}
								</Tag>
								{user.status === "active" ? (
									<Tag variant="success">Actif</Tag>
								) : user.status === "suspended" ? (
									<Tag variant="warning">Suspendu</Tag>
								) : (
									<Tag variant="neutral">Archivé</Tag>
								)}
								{mfa && mfa.length > 0 && (
									<Tag variant="success" noDot>
										<AdminIcon n="check" s={11} /> MFA activée
									</Tag>
								)}
							</div>
							<div className="muted" style={{ fontSize: 12 }}>
								{user.email} · Dernière connexion :{" "}
								{user.lastLoginAt
									? new Date(user.lastLoginAt).toLocaleString("fr-FR")
									: "jamais"}
							</div>
						</div>
					</div>
				</CardBody>
			</Card>

			<Segmented
				value={tab}
				onChange={setTab}
				className="mb-4"
				options={[
					{ value: "identity", label: "Identité" },
					{ value: "permissions", label: "Permissions" },
					{ value: "sessions", label: "Sessions" },
					{ value: "audit", label: "Audit" },
				]}
			/>

			{tab === "identity" && (
				<Card>
					<CardHead title="Informations" />
					<CardBody>
						<div className="info-row">
							<span className="l">Email</span>
							<span className="v mono">{user.email}</span>
						</div>
						<div className="info-row">
							<span className="l">Téléphone</span>
							<span className="v mono">{user.phone ?? "—"}</span>
						</div>
						<div className="info-row">
							<span className="l">Langue</span>
							<span className="v">{user.lang.toUpperCase()}</span>
						</div>
						<div className="info-row">
							<span className="l">Fuseau horaire</span>
							<span className="v">{user.timezone}</span>
						</div>
						<div className="info-row">
							<span className="l">Échecs de connexion</span>
							<span className="v tn">{user.failedAttempts ?? 0}</span>
						</div>
						<div className="info-row" style={{ borderBottom: "none" }}>
							<span className="l">Verrouillé jusqu'à</span>
							<span className="v">
								{user.lockedUntil
									? new Date(user.lockedUntil).toLocaleString("fr-FR")
									: "Non"}
							</span>
						</div>
					</CardBody>
				</Card>
			)}

			{tab === "permissions" && (
				<div className="stack">
					<Card>
						<CardHead title={`Rôle principal · ${user.role.label}`} />
						<CardBody>
							<div className="muted" style={{ fontSize: 12 }}>
								Les permissions du rôle sont héritées automatiquement. Pour
								ajouter des permissions exceptionnelles, utilisez la section
								ci-dessous.
							</div>
						</CardBody>
					</Card>
					<Card>
						<CardHead
							title={`Permissions additionnelles · ${user.additionalPermissions.length}`}
							sub="Permissions atomiques hors rôle (CDC §4.5.3)"
						/>
						<CardBody>
							{user.additionalPermissions.length === 0 ? (
								<div className="empty">Aucune permission additionnelle.</div>
							) : (
								<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
									{user.additionalPermissions.map((p) => (
										<span
											key={p.key}
											className="mono"
											style={{
												fontSize: 11,
												padding: "4px 8px",
												background: "var(--a-surface-2)",
												borderRadius: 4,
											}}
										>
											{p.key}
											{p.expiresAt && (
												<span className="muted" style={{ marginLeft: 6 }}>
													(expire{" "}
													{new Date(p.expiresAt).toLocaleDateString("fr-FR")})
												</span>
											)}
										</span>
									))}
								</div>
							)}
						</CardBody>
					</Card>
				</div>
			)}

			{tab === "sessions" && (
				<Card>
					<CardHead title="Sessions actives" />
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Appareil</th>
									<th>IP</th>
									<th>Dernière activité</th>
									<th>Expire</th>
								</tr>
							</thead>
							<tbody>
								{(!sessions || sessions.length === 0) && (
									<tr>
										<td colSpan={4} className="muted center">
											Aucune session active.
										</td>
									</tr>
								)}
								{sessions?.map((s) => (
									<tr key={s.id}>
										<td>
											<strong style={{ fontSize: 12 }}>
												{shortUa(s.userAgent)}
											</strong>
											<div className="mono muted" style={{ fontSize: 10 }}>
												{s.id.slice(0, 8)}
											</div>
										</td>
										<td className="mono">{s.ip ?? "—"}</td>
										<td className="muted">
											{new Date(s.lastActivityAt).toLocaleString("fr-FR")}
										</td>
										<td className="muted">
											{new Date(s.expiresAt).toLocaleDateString("fr-FR")}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			{tab === "audit" && (
				<Card>
					<CardHead
						title="Journal d'audit personnel"
						sub="30 dernières actions"
					/>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Horodatage</th>
									<th>Action</th>
									<th>Cible</th>
									<th>Détail</th>
									<th>IP</th>
								</tr>
							</thead>
							<tbody>
								{(!audit || audit.length === 0) && (
									<tr>
										<td colSpan={5} className="muted center">
											Aucune action enregistrée.
										</td>
									</tr>
								)}
								{audit?.map((a) => (
									<tr key={a.id}>
										<td className="mono" style={{ fontSize: 11 }}>
											{new Date(a.at).toLocaleString("fr-FR")}
										</td>
										<td>
											<Tag
												variant={a.result === "success" ? "info" : "danger"}
												noDot
												className={cn("mono")}
											>
												{a.action}
											</Tag>
										</td>
										<td className="mono" style={{ fontSize: 11 }}>
											{a.target ?? "—"}
										</td>
										<td className="muted" style={{ fontSize: 11 }}>
											{a.detail ?? "—"}
										</td>
										<td className="mono muted" style={{ fontSize: 11 }}>
											{a.ip ?? "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}
		</>
	);
}

function shortUa(ua: string | null): string {
	if (!ua) return "Inconnu";
	if (ua.includes("Chrome")) return "Chrome / Chromium";
	if (ua.includes("Firefox")) return "Firefox";
	if (ua.includes("Safari")) return "Safari";
	if (ua.startsWith("curl")) return "curl";
	return ua.slice(0, 40);
}
